import { isProduction } from '@/config/environment';
import { isExcludedEmail } from '@/config/excluded-emails';

declare global {
  interface Window {
    dataLayer?: unknown[];
    _hsq?: unknown[];
  }
}

type GtmEventData = Record<
  string,
  string | number | boolean | null | undefined
>;

export const GoogleTagId = {
  createAgentClicked: 'create-agent-clicked',
  createAvatarClicked: 'create-avatar-clicked',
  createVideoClicked: 'create-video-clicked',
  createApiKey: 'create-api-key',
  studioApiDocs: 'studio-api-docs',
  stockAgentOpened: 'stock-agent-opened',
  knowledgeBaseCreated: 'knowledge-base-created',
} as const;

const GA4_MEASUREMENT_ID = 'G-3S4PWHSDWL';
const GA4_LINKER_DOMAINS = ['beyondpresence.ai', 'bey.chat'];
const GTM_CONTAINER_ID = 'GTM-KQ3GSVNV';
const HUBSPOT_SCRIPT_SRC = '//js-eu1.hs-scripts.com/146556213.js';
const CLARITY_PROJECT_ID = 't7ikvdmbdy';

const loadHubSpotScript = () => {
  if (document.getElementById('hs-script-loader')) return;

  window._hsq = window._hsq ?? [];
  window._hsq.push(['setAutoCollectForms', false]);

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.id = 'hs-script-loader';
  script.async = true;
  script.defer = true;
  script.src = HUBSPOT_SCRIPT_SRC;

  document.head.appendChild(script);
};

const loadClarityScript = () => {
  if (document.getElementById('clarity-script-loader')) return;

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.id = 'clarity-script-loader';
  script.innerHTML = `
    (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${CLARITY_PROJECT_ID}");
  `;

  document.head.appendChild(script);
};

const loadGA4Script = () => {
  if (document.getElementById('ga4-script-loader')) return;

  const script = document.createElement('script');
  script.async = true;
  script.id = 'ga4-script-loader';
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;

  const inlineScript = document.createElement('script');
  inlineScript.id = 'ga4-config-script';
  inlineScript.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${GA4_MEASUREMENT_ID}', {
      'linker': {
        'domains': ${JSON.stringify(GA4_LINKER_DOMAINS)}
      }
    });
  `;

  document.head.appendChild(script);
  document.head.appendChild(inlineScript);
};

const loadGTMScript = () => {
  if (document.getElementById('gtm-script-loader')) return;

  const script = document.createElement('script');
  script.id = 'gtm-script-loader';
  script.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${GTM_CONTAINER_ID}');
  `;
  document.head.insertBefore(script, document.head.firstChild);

  const noscript = document.createElement('noscript');
  noscript.id = 'gtm-noscript-loader';
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_CONTAINER_ID}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
};

export const loadMarketingTags = () => {
  if (!isProduction) return;

  loadHubSpotScript();
  loadClarityScript();
  loadGA4Script();
  loadGTMScript();
};

export const syncHubSpotTracking = (email: string | null | undefined) => {
  if (!isProduction || !email) return;

  window._hsq = window._hsq ?? [];

  if (isExcludedEmail(email)) {
    window._hsq.push(['doNotTrack']);
    return;
  }

  window._hsq.push(['doNotTrack', { track: true }]);
  window._hsq.push(['identify', { email }]);
  window._hsq.push(['trackPageView']);
};

export const pushGtmEvent = (event: string, data?: GtmEventData) => {
  if (!isProduction) return;
  if (typeof data?.email === 'string' && isExcludedEmail(data.email)) return;

  const params = Object.fromEntries(
    Object.entries(data ?? {}).filter(([, value]) => value != null),
  );

  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ event, ...params });
};
